import React, { useRef, useState, useCallback, useEffect } from 'react';
import { FL_SCRIPT } from '../shared/fiveLeague';
import './HandwritingCanvas.css';

// 手書き認識が利用可能かチェック
async function checkHandwritingSupport() {
  if (!('createHandwritingRecognizer' in navigator)) {
    return { supported: false, reason: 'API not available' };
  }

  try {
    const result = await navigator.queryHandwritingRecognizer({
      languages: ['ja'],
    });
    return {
      supported: result !== null,
      reason: result ? 'supported' : 'Japanese not supported',
    };
  } catch (e) {
    return { supported: false, reason: e.message };
  }
}

// ネイティブ手書き認識
async function recognizeWithNativeAPI(strokes, script) {
  try {
    const recognizer = await navigator.createHandwritingRecognizer({
      languages: ['ja'],
    });

    const drawing = recognizer.startDrawing({
      recognitionType: 'text',
      inputType: 'touch',
      textContext: '',
      alternatives: 5,
    });

    // ストロークを追加
    for (const stroke of strokes) {
      const hwStroke = new HandwritingStroke();
      for (let i = 0; i < stroke.x.length; i++) {
        hwStroke.addPoint({
          x: stroke.x[i],
          y: stroke.y[i],
          t: stroke.t[i],
        });
      }
      drawing.addStroke(hwStroke);
    }

    const predictions = await drawing.getPrediction();
    drawing.clear();
    recognizer.finish();

    if (predictions && predictions.length > 0) {
      // 1文字ずつに分解して返す
      const candidates = [];
      for (const pred of predictions) {
        const text = pred.text;
        if (text && text.length > 0) {
          // 最初の文字を候補に追加
          const char = text.charAt(0);
          if (!candidates.includes(char)) {
            candidates.push(char);
          }
        }
      }
      return candidates.slice(0, 5);
    }
    return null;
  } catch (e) {
    console.warn('Native handwriting recognition failed:', e);
    return null;
  }
}

// Google Input Tools API (CORS対応版 - jsonp使用)
async function recognizeWithGoogleAPI(strokes, script) {
  // ストロークデータをGoogle API形式に変換
  const ink = strokes.map(s => [s.x, s.y, []]);

  const requestData = {
    app_version: 0.4,
    api_level: '537.36',
    device: window.navigator.userAgent,
    input_type: 0,
    options: 'enable_pre_space',
    requests: [{
      writing_guide: {
        writing_area_width: 250,
        writing_area_height: 250,
      },
      ink: ink,
      language: 'ja',
    }],
  };

  try {
    // CORS回避のため、直接POSTを試みる
    const response = await fetch(
      'https://inputtools.google.com/request?itc=ja-t-i0-handwrit&app=doodle&num=5',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
        mode: 'cors',
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (data && data[1] && data[1][0] && data[1][0][1]) {
        return data[1][0][1].slice(0, 5);
      }
    }
    return null;
  } catch (e) {
    console.warn('Google API failed (CORS):', e.message);
    return null;
  }
}

// フォールバック: ダミー認識（文字種に応じた候補）
function fallbackRecognize(script) {
  const candidates = {
    [FL_SCRIPT.HIRAGANA]: ['あ', 'い', 'う', 'え', 'お', 'か', 'き', 'く', 'け', 'こ', 'さ', 'し', 'す', 'せ', 'そ', 'た', 'ち', 'つ', 'て', 'と', 'な', 'に', 'ぬ', 'ね', 'の', 'は', 'ひ', 'ふ', 'へ', 'ほ', 'ま', 'み', 'む', 'め', 'も', 'や', 'ゆ', 'よ', 'ら', 'り', 'る', 'れ', 'ろ', 'わ', 'を', 'ん'],
    [FL_SCRIPT.KATAKANA]: ['ア', 'イ', 'ウ', 'エ', 'オ', 'カ', 'キ', 'ク', 'ケ', 'コ', 'サ', 'シ', 'ス', 'セ', 'ソ', 'タ', 'チ', 'ツ', 'テ', 'ト', 'ナ', 'ニ', 'ヌ', 'ネ', 'ノ', 'ハ', 'ヒ', 'フ', 'ヘ', 'ホ', 'マ', 'ミ', 'ム', 'メ', 'モ', 'ヤ', 'ユ', 'ヨ', 'ラ', 'リ', 'ル', 'レ', 'ロ', 'ワ', 'ヲ', 'ン', 'ー'],
    [FL_SCRIPT.KANJI]: ['山', '川', '田', '森', '林', '大', '小', '中', '上', '下', '日', '月', '火', '水', '木', '金', '土', '天', '地', '人', '東', '西', '南', '北', '海', '空', '花', '鳥', '風', '雲'],
    [FL_SCRIPT.NUMBER]: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
    [FL_SCRIPT.ALPHABET]: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'],
  };

  const pool = candidates[script] || candidates[FL_SCRIPT.HIRAGANA];
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 5);
}

function HandwritingCanvas({ onRecognize, script, disabled }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [inputMode, setInputMode] = useState('draw');
  const [inputValue, setInputValue] = useState('');
  const [recognizing, setRecognizing] = useState(false);
  const [apiStatus, setApiStatus] = useState('checking');
  const strokesRef = useRef([]); // ストロークデータを保持
  const currentStrokeRef = useRef({ x: [], y: [], t: [] });

  // API対応チェック
  useEffect(() => {
    checkHandwritingSupport().then(result => {
      setApiStatus(result.supported ? 'native' : 'fallback');
    });
  }, []);

  // キャンバス初期化
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  // 座標取得
  const getCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
      t: Date.now(),
    };
  };

  // 描画開始
  const startDrawing = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y, t } = getCoordinates(e, canvas);

    ctx.beginPath();
    ctx.moveTo(x, y);

    currentStrokeRef.current = { x: [x], y: [y], t: [t] };
    setIsDrawing(true);
    setHasStrokes(true);
  }, [disabled]);

  // 描画中
  const draw = useCallback((e) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y, t } = getCoordinates(e, canvas);

    ctx.lineTo(x, y);
    ctx.stroke();

    currentStrokeRef.current.x.push(x);
    currentStrokeRef.current.y.push(y);
    currentStrokeRef.current.t.push(t);
  }, [isDrawing, disabled]);

  // 描画終了
  const endDrawing = useCallback((e) => {
    if (!isDrawing) return;
    e?.preventDefault();

    // ストロークを保存
    if (currentStrokeRef.current.x.length > 0) {
      strokesRef.current.push({ ...currentStrokeRef.current });
    }
    currentStrokeRef.current = { x: [], y: [], t: [] };
    setIsDrawing(false);
  }, [isDrawing]);

  // キャンバスクリア
  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    strokesRef.current = [];
    setHasStrokes(false);
  }, []);

  // 認識実行
  const handleRecognize = useCallback(async () => {
    if (inputMode === 'keyboard') {
      if (inputValue) {
        onRecognize([inputValue.charAt(0)]);
      }
      return;
    }

    if (!hasStrokes || strokesRef.current.length === 0) return;

    setRecognizing(true);
    let results = null;

    try {
      // 1. ネイティブAPIを試行
      if (apiStatus === 'native') {
        results = await recognizeWithNativeAPI(strokesRef.current, script);
      }

      // 2. Google APIを試行
      if (!results) {
        results = await recognizeWithGoogleAPI(strokesRef.current, script);
      }

      // 3. フォールバック
      if (!results || results.length === 0) {
        results = fallbackRecognize(script);
      }

      onRecognize(results);
    } catch (e) {
      console.error('Recognition error:', e);
      onRecognize(fallbackRecognize(script));
    } finally {
      setRecognizing(false);
    }
  }, [hasStrokes, inputMode, inputValue, script, apiStatus, onRecognize]);

  // キーボード入力変更
  const handleInputChange = (e) => {
    const val = e.target.value;
    if (val.length <= 1) {
      setInputValue(val);
    }
  };

  // キーボードで確定
  const handleKeyboardSubmit = () => {
    if (inputValue) {
      onRecognize([inputValue]);
    }
  };

  const getScriptPlaceholder = () => {
    switch (script) {
      case FL_SCRIPT.HIRAGANA: return 'あ';
      case FL_SCRIPT.KATAKANA: return 'ア';
      case FL_SCRIPT.KANJI: return '漢';
      case FL_SCRIPT.NUMBER: return '1';
      case FL_SCRIPT.ALPHABET: return 'A';
      default: return '文字';
    }
  };

  return (
    <div className="canvas-container">
      {/* 手書きキャンバス（メイン） */}
      <div className="canvas-wrapper">
        <canvas
          ref={canvasRef}
          width={300}
          height={300}
          className="drawing-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={endDrawing}
          onMouseLeave={endDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={endDrawing}
        />
        {!hasStrokes && inputMode === 'draw' && (
          <div className="canvas-placeholder">
            <span className="placeholder-text">{getScriptPlaceholder()}</span>
            <span className="placeholder-hint">ここに書いてね</span>
          </div>
        )}
      </div>

      {inputMode === 'draw' ? (
        <>
          <div className="canvas-actions">
            <button
              className="btn-secondary action-btn"
              onClick={clearCanvas}
              disabled={disabled || !hasStrokes}
            >
              消す
            </button>
            <button
              className="btn-primary action-btn recognize-btn"
              onClick={handleRecognize}
              disabled={disabled || !hasStrokes || recognizing}
            >
              {recognizing ? '認識中...' : 'よみとる'}
            </button>
          </div>

          {/* キーボードへの切り替えリンク（補助的） */}
          <button
            className="keyboard-fallback-link"
            onClick={() => setInputMode('keyboard')}
            disabled={disabled}
          >
            手書きがうまくいかない場合 →
          </button>
        </>
      ) : (
        <div className="keyboard-input-section">
          <p className="keyboard-hint">キーボードで直接入力</p>
          <input
            type="text"
            className="keyboard-input"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={getScriptPlaceholder()}
            maxLength={1}
            disabled={disabled}
            autoFocus
          />
          <button
            className="btn-primary keyboard-submit-btn"
            onClick={handleKeyboardSubmit}
            disabled={disabled || !inputValue}
          >
            この文字で確定
          </button>
          <button
            className="back-to-draw-link"
            onClick={() => { setInputMode('draw'); setInputValue(''); }}
            disabled={disabled}
          >
            ← 手書きに戻る
          </button>
        </div>
      )}
    </div>
  );
}

export default HandwritingCanvas;
